/**
 * Navigation configuration using centralized role-based access control.
 * Defines navigation items, icons, and access rules using the centralized role system.
 * Update when adding new routes or modifying navigation structure.
 */
import type { SvgIconComponent } from '@mui/icons-material';
import {
  Assignment,
  Business,
  Campaign,
  ChecklistRounded,
  Class as ClassIcon,
  ConfirmationNumber,
  Description,
  EventAvailable,
  FactCheck,
  Flag,
  Forum,
  Group,
  Groups,
  Handshake,
  HelpOutline,
  HistoryRounded,
  Insights,
  LibraryBooks,
  Login,
  Logout,
  ManageHistory,
  Menu as MenuIcon,
  OutlinedFlag,
  Payments,
  People,
  PersonAdd,
  RateReview,
  ReceiptLong,
  School,
  Sensors,
  Settings,
  VideoCall,
  Videocam,
  VpnKey,
} from '@mui/icons-material';

import { FEATURE_FLAGS } from '@shared/featureFlags.js';
import type { RoleId } from '@shared/roles.js';
import type { TeamPolicy } from '@shared/teamPolicy.js';
import { ROLES, SUPER_ADMIN_FEATURE_BYPASS } from '@/config/roles.js';
import { hasRouteAccess } from '@/utils/accessControl.js';
import { NAV_OPEN_STATE } from '@/utils/teacherEventSource.js';
import {
  hasEffectiveFeatureAccess,
  isRouteHiddenByTeamPolicy,
} from '@/utils/teamPolicyAccess.js';

export const DRAWER_WIDTH = 240;

/** Width of the desktop nav when collapsed to the icon rail. */
export const NAV_RAIL_WIDTH = 72;

/** Height of the phone bottom bar's button row, before its safe-area padding. */
export const BOTTOM_NAV_BAR_HEIGHT = 56;

/**
 * Custom property MobileBottomNavigation sets to its full height (button row
 * plus bottom safe-area padding) for as long as the bar is on screen.
 */
export const BOTTOM_NAV_HEIGHT_VAR = '--bottom-nav-height';

/**
 * How much of the viewport's bottom edge the phone bottom bar covers, as a CSS
 * length: its height while it is on screen, 0 everywhere else. The bar is
 * `position: fixed` above the page, so anything pinned to the bottom of the
 * viewport (a sticky CTA, a fixed action bar, a floating button) offsets by
 * this — `bottom: BOTTOM_NAV_OFFSET`, or inside a `calc()` — rather than
 * guessing the bar's height or whether it is showing.
 */
export const BOTTOM_NAV_OFFSET = `var(${BOTTOM_NAV_HEIGHT_VAR}, 0px)`;

/**
 * Builder-style routes whose working canvas needs the horizontal space
 * (docked side panels, two-pane pickers): the app shell trades its outer
 * gutters (and the lg content cap) for canvas width here (App.tsx).
 * Sub-routes inherit, except the homework Save as PDF preview: a document
 * with no canvas, which keeps the ordinary shell. The nav-rail collapse is
 * NOT keyed on these routes — their pages report crafting mode via
 * BuilderCanvasActiveContext, and useNavRail rails only when the collapse is
 * what lets the tray dock.
 */
const BUILDER_CANVAS_ROUTES = ['/lesson-prep', '/teacher/homework'];
const HOMEWORK_PRINT_PREVIEW = /^\/teacher\/homework\/[^/]+\/print\/?$/;

export const isBuilderCanvasRoute = (pathname: string): boolean =>
  !HOMEWORK_PRINT_PREVIEW.test(pathname) &&
  BUILDER_CANVAS_ROUTES.some(
    route => pathname === route || pathname.startsWith(`${route}/`)
  );

/**
 * Active-tab predicate shared by the bottom bar and the drawer: a nav item owns
 * its sub-routes, so `/practice/topic/x` keeps Practice lit and
 * `/history/session/x` keeps History lit. Exact-match-only left a student
 * staring at an unlit bar for most of a session. Routes owned by no item
 * (`/homework/:id`, reached from several tabs) match nothing — deliberately.
 *
 * A record shared by two sections is owned by the one it was opened from, not
 * by the one its path sits under. `useNavSection` resolves that origin only
 * on user records, keeping their validators out of the common app shell.
 */
export const isNavItemActive = (
  pathname: string | undefined,
  path: string | undefined,
  section?: string
): boolean => {
  if (!pathname || !path) return false;
  if (section !== undefined) return section === path;
  return pathname === path || pathname.startsWith(`${path}/`);
};

/**
 * Shared transition for surfaces that resize with the nav rail (drawer paper,
 * main content, header logo offset). Keeping every consumer on the same curve
 * makes the expand/collapse read as one motion.
 */
export const navWidthTransitionSx = (property: 'width' | 'margin-left') => ({
  transition: `${property} 200ms var(--ease-default, ease-out)`,
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
});

export interface NavUser {
  role?: string;
  featureFlags?: Map<string, boolean>;
  teamPolicy?: TeamPolicy | null;
}

type NavGroupId =
  | 'leads-and-enrollment'
  | 'accounts-and-access'
  | 'content-and-review'
  | 'teaching-and-records'
  | 'partners-and-platform'
  | 'support-and-my-tools';

interface NavGroup {
  id: NavGroupId;
  label: string;
  order: number;
}

export interface NavigationItem {
  id: string;
  path: string;
  search?: string;
  /**
   * Router state a click carries to the page, for pages that record how they
   * were opened (the Students class page's `class_page_open` source).
   */
  state?: Readonly<Record<string, unknown>>;
  label: string;
  icon: SvgIconComponent;
  showInBottomNav: boolean;
  bottomNavLabel?: string;
  adminBottomNav?: boolean;
  adminBottomNavOrder?: number;
  requiredFeature?: string;
  group?: NavGroupId;
}

export interface AuthItem {
  id: string;
  label: string;
  icon: SvgIconComponent;
}

export interface GroupedNavSection {
  groupId: NavGroupId;
  groupLabel: string;
  items: NavigationItem[];
}

interface MoreMenuItem {
  id: 'more';
  label: 'More';
  icon: SvgIconComponent;
}

/**
 * Navigation group IDs for organising items by function.
 * Order here defines display order in the drawer.
 */
const NAV_GROUPS = {
  LEADS_AND_ENROLLMENT: 'leads-and-enrollment' as NavGroupId,
  ACCOUNTS_AND_ACCESS: 'accounts-and-access' as NavGroupId,
  CONTENT_AND_REVIEW: 'content-and-review' as NavGroupId,
  TEACHING_AND_RECORDS: 'teaching-and-records' as NavGroupId,
  PARTNERS_AND_PLATFORM: 'partners-and-platform' as NavGroupId,
  SUPPORT_AND_MY_TOOLS: 'support-and-my-tools' as NavGroupId,
};

/**
 * Group metadata: label and display order (lower = first). The grouped drawer
 * is admin/super_admin only (NavigationDrawer), so these sections describe the
 * staff day: the money pipeline first, then accounts, then content, teaching,
 * platform config, and last the surfaces a staff member uses as a participant.
 */
const NAVIGATION_GROUPS: NavGroup[] = [
  {
    id: NAV_GROUPS.LEADS_AND_ENROLLMENT,
    label: 'Leads & enrollment',
    order: 0,
  },
  { id: NAV_GROUPS.ACCOUNTS_AND_ACCESS, label: 'Accounts & access', order: 1 },
  { id: NAV_GROUPS.CONTENT_AND_REVIEW, label: 'Content & review', order: 2 },
  {
    id: NAV_GROUPS.TEACHING_AND_RECORDS,
    label: 'Teaching & records',
    order: 3,
  },
  {
    id: NAV_GROUPS.PARTNERS_AND_PLATFORM,
    label: 'Partners & platform',
    order: 4,
  },
  {
    id: NAV_GROUPS.SUPPORT_AND_MY_TOOLS,
    label: 'Support & my tools',
    order: 5,
  },
];

/**
 * Navigation item definitions with route-based access control and optional group.
 *
 * Definition order is the display order *within* a group (groupItemsByNavGroup
 * keeps it, and admin/super_admin have no ROLE_NAV_ORDER entry), so the keys
 * below read top-to-bottom exactly as the super-admin drawer does. Keep an item
 * next to its section-mates when adding one.
 */
const NAVIGATION_ITEMS: Record<string, NavigationItem> = {
  // --- Leads & enrollment: lead → class → roster → attendance → money ------
  LEADS_ADMIN: {
    id: 'leads-admin',
    path: '/admin/leads',
    label: 'Leads Management',
    icon: Business,
    showInBottomNav: false,
    adminBottomNav: true,
    adminBottomNavOrder: 0,
    bottomNavLabel: 'Leads',
    group: NAV_GROUPS.LEADS_AND_ENROLLMENT,
  },
  CLASS_CODES_ADMIN: {
    id: 'class-codes-admin',
    path: '/admin/class-codes',
    label: 'Class Codes',
    icon: VpnKey,
    showInBottomNav: false,
    adminBottomNav: true,
    adminBottomNavOrder: 2,
    group: NAV_GROUPS.LEADS_AND_ENROLLMENT,
  },
  LIVE_CLASSES_ADMIN: {
    id: 'live-classes-admin',
    path: '/admin/live',
    label: 'Live Classes',
    icon: Sensors,
    showInBottomNav: false,
    group: NAV_GROUPS.LEADS_AND_ENROLLMENT,
  },
  ENROLLMENTS_ADMIN: {
    id: 'enrollments-admin',
    path: '/admin/enrollments',
    label: 'Enrollments',
    icon: PersonAdd,
    showInBottomNav: false,
    adminBottomNav: true,
    adminBottomNavOrder: 1,
    group: NAV_GROUPS.LEADS_AND_ENROLLMENT,
  },
  // The weekly marking inbox. Named "Attendance" everywhere a human reads it
  // (§11 naming decision); the model behind it is still ClassSession.
  ATTENDANCE_ADMIN: {
    id: 'attendance-admin',
    path: '/admin/attendance',
    label: 'Attendance',
    icon: FactCheck,
    showInBottomNav: false,
    group: NAV_GROUPS.LEADS_AND_ENROLLMENT,
  },
  // The cross-class money chase. There is deliberately NO nav entry for the
  // per-class page (`/admin/classes/:classCodeId`): its path is a pattern, so a
  // nav link built from it would point at a URL that does not exist. It is
  // reached by drilling in from a class list or a payments-due row.
  PAYMENTS_DUE_ADMIN: {
    id: 'payments-due-admin',
    path: '/admin/billing',
    label: 'Payments Due',
    icon: Payments,
    showInBottomNav: false,
    group: NAV_GROUPS.LEADS_AND_ENROLLMENT,
  },

  // --- Accounts & access: who exists, and what they are let into ----------
  USER_MANAGEMENT: {
    id: 'user-management',
    path: '/admin/users',
    label: 'Users',
    icon: People,
    showInBottomNav: false,
    group: NAV_GROUPS.ACCOUNTS_AND_ACCESS,
  },
  NONPROFIT_ORGANIZATIONS_ADMIN: {
    id: 'nonprofit-organizations-admin',
    path: '/admin/nonprofit-organizations',
    label: 'Nonprofit Organizations',
    icon: Groups,
    showInBottomNav: false,
    group: NAV_GROUPS.ACCOUNTS_AND_ACCESS,
  },
  // Placement pools are an onboarding lever — which cohort gets which
  // cold-start diagnostic — so they sit with the accounts they gate, not with
  // the authored content they draw from.
  DIAGNOSTIC_POOLS_ADMIN: {
    id: 'diagnostic-pools-admin',
    path: '/admin/diagnostic-pools',
    label: 'Diagnostic Pools',
    icon: Insights,
    showInBottomNav: false,
    group: NAV_GROUPS.ACCOUNTS_AND_ACCESS,
  },
  ACCESS_CODES_ADMIN: {
    id: 'access-codes-admin',
    path: '/admin/access-codes',
    label: 'Access Codes',
    icon: ConfirmationNumber,
    showInBottomNav: false,
    group: NAV_GROUPS.ACCOUNTS_AND_ACCESS,
  },

  // --- Content & review: authoring, then the queues it feeds --------------
  LESSON_PREP: {
    id: 'lesson-prep',
    path: '/lesson-prep',
    label: 'Lesson Prep',
    icon: Description,
    showInBottomNav: false,
    group: NAV_GROUPS.CONTENT_AND_REVIEW,
  },
  ADAPTIVE_TEMPLATES_ADMIN: {
    id: 'adaptive-templates-admin',
    path: '/admin/adaptive-templates',
    label: 'Adaptive Templates',
    icon: RateReview,
    showInBottomNav: false,
    group: NAV_GROUPS.CONTENT_AND_REVIEW,
  },
  QUESTION_FEEDBACK_ADMIN: {
    id: 'question-feedback-admin',
    path: '/admin/question-feedback',
    label: 'Question Flags',
    icon: OutlinedFlag,
    showInBottomNav: false,
    group: NAV_GROUPS.CONTENT_AND_REVIEW,
  },
  TEACHER_QUESTIONS_ADMIN: {
    id: 'teacher-questions-admin',
    path: '/admin/teacher-questions',
    label: 'Teacher Questions',
    icon: LibraryBooks,
    showInBottomNav: false,
    group: NAV_GROUPS.CONTENT_AND_REVIEW,
  },

  // --- Teaching & records: the classes being run and the paper they leave -
  // The teacher's landing and the first tab of their phone bar. A home team
  // whose policy hides class codes (a nonprofit cohort's, whose teachers are
  // kept out of mainstream classes) never sees it, so Students leads their
  // bar. "Classes" fits a fifth of a 320px bar, as "Requests" does.
  TEACHER_CLASS_CODES: {
    id: 'teacher-class-codes',
    path: '/teacher/class-codes',
    label: 'My Classes',
    icon: ClassIcon,
    showInBottomNav: true,
    bottomNavLabel: 'Classes',
    group: NAV_GROUPS.TEACHING_AND_RECORDS,
  },
  // The night-before check: did they do it, what to go over, whom to chase.
  // Every teacher role and super_admin (the picker) reach it, and it sits on
  // the teacher's phone bar because that check happens on a phone (design
  // review DR3): second after My Classes, first where class codes are hidden.
  // Clicks tell the page they came from the nav.
  TEACHER_STUDENTS: {
    id: 'teacher-students',
    path: '/teacher/students',
    state: NAV_OPEN_STATE,
    label: 'Students',
    icon: Group,
    showInBottomNav: true,
    bottomNavLabel: 'Students',
    group: NAV_GROUPS.TEACHING_AND_RECORDS,
  },
  WORKSHEETS_ADMIN: {
    id: 'worksheets-admin',
    path: '/admin/worksheets',
    label: 'All Worksheets',
    icon: Description,
    showInBottomNav: false,
    group: NAV_GROUPS.TEACHING_AND_RECORDS,
  },
  HOMEWORK_ADMIN: {
    id: 'homework-admin',
    path: '/admin/homework',
    label: 'All Homework',
    icon: Assignment,
    showInBottomNav: false,
    group: NAV_GROUPS.TEACHING_AND_RECORDS,
  },
  HISTORY: {
    id: 'history',
    path: '/history',
    label: 'History',
    icon: HistoryRounded,
    showInBottomNav: true,
    bottomNavLabel: 'History',
    group: NAV_GROUPS.TEACHING_AND_RECORDS,
  },
  TEACHER_STATEMENTS_ADMIN: {
    id: 'teacher-statements-admin',
    path: '/admin/teacher-statements',
    label: 'Teacher Statements',
    icon: ReceiptLong,
    showInBottomNav: false,
    group: NAV_GROUPS.TEACHING_AND_RECORDS,
  },
  REVENUE_ADMIN: {
    id: 'revenue-admin',
    path: '/admin/revenue',
    label: 'Revenue',
    icon: ReceiptLong,
    showInBottomNav: false,
    group: NAV_GROUPS.TEACHING_AND_RECORDS,
  },
  // Issuing each teacher their private ICS link. Sits beside the other
  // teacher-facing ops rows, not under Zoom: the thing being managed is the
  // teacher's subscription, not an integration credential.
  TEACHER_CALENDARS_ADMIN: {
    id: 'teacher-calendars-admin',
    path: '/admin/teacher-calendar-feeds',
    label: 'Teacher Calendars',
    icon: EventAvailable,
    showInBottomNav: false,
    group: NAV_GROUPS.TEACHING_AND_RECORDS,
  },

  // --- Partners & platform: outside relationships and system config -------
  PARTNERS_ADMIN: {
    id: 'partners-admin',
    path: '/admin/partners',
    label: 'Partners',
    icon: Handshake,
    showInBottomNav: false,
    group: NAV_GROUPS.PARTNERS_AND_PLATFORM,
  },
  // The marketing partner's own surface, and the super-admin's window onto it.
  // Campaign, not Handshake — the latter is PARTNERS_ADMIN's icon, and Campaign
  // is already the affiliate role's mark in the header role chip. The group
  // places it beside Partners for super_admin; the affiliate never sees a group
  // because the grouped drawer is admin/super_admin only.
  AFFILIATE_PORTAL: {
    id: 'affiliate-portal',
    path: '/affiliate',
    label: 'Affiliate Portal',
    icon: Campaign,
    // The affiliate's one and only tab. Super_admin's bottom bar is the curated
    // adminBottomNav set, which this is deliberately not part of.
    showInBottomNav: true,
    bottomNavLabel: 'Portal',
    group: NAV_GROUPS.PARTNERS_AND_PLATFORM,
  },
  PRODUCT_ANALYTICS: {
    id: 'product-analytics',
    path: '/admin/product-analytics',
    label: 'Product Adoption',
    icon: Insights,
    showInBottomNav: false,
    group: NAV_GROUPS.PARTNERS_AND_PLATFORM,
  },
  // Beside Zoom Accounts, not with the content listings: an admin reading the
  // activity log got there from the accounts page. No role field — visibility
  // derives from ROUTE_ACCESS via hasRouteAccess in getNavigationItems, and
  // this route is [ADMIN, SUPER_ADMIN] where Zoom Accounts is super-admin only.
  ZOOM_ACTIVITY_ADMIN: {
    id: 'zoom-activity-admin',
    path: '/admin/zoom-activity',
    label: 'Zoom Activity',
    icon: ManageHistory,
    showInBottomNav: false,
    group: NAV_GROUPS.PARTNERS_AND_PLATFORM,
  },
  FEATURE_FLAG_ADMIN: {
    id: 'feature-flag-admin',
    path: '/admin/feature-flags',
    label: 'Feature Flags',
    icon: Flag,
    showInBottomNav: false,
    requiredFeature: FEATURE_FLAGS.ADMIN_FEATURE_MANAGEMENT.id,
    group: NAV_GROUPS.PARTNERS_AND_PLATFORM,
  },
  ZOOM_ACCOUNTS_ADMIN: {
    id: 'zoom-accounts-admin',
    path: '/admin/zoom-accounts',
    label: 'Zoom Accounts',
    // VideoCall, not Videocam — the latter is JOIN_CLASS's icon.
    icon: VideoCall,
    showInBottomNav: false,
    group: NAV_GROUPS.PARTNERS_AND_PLATFORM,
  },

  // --- Support & my tools: the app as a participant sees it ---------------
  // Staff-side inbox, kept next to MY_REQUESTS so both halves of a help thread
  // sit together.
  HELP_REQUESTS: {
    id: 'help-requests',
    path: '/help-requests',
    label: 'Help Requests',
    icon: Forum,
    showInBottomNav: true,
    bottomNavLabel: 'Requests',
    group: NAV_GROUPS.SUPPORT_AND_MY_TOOLS,
  },
  TEACHER_QUESTIONS: {
    id: 'teacher-questions',
    path: '/teacher/questions',
    label: 'My Questions',
    icon: LibraryBooks,
    showInBottomNav: false,
    group: NAV_GROUPS.SUPPORT_AND_MY_TOOLS,
  },
  MY_REQUESTS: {
    id: 'my-requests',
    path: '/my-requests',
    label: 'My Requests',
    icon: HelpOutline,
    showInBottomNav: true,
    bottomNavLabel: 'Requests',
    group: NAV_GROUPS.SUPPORT_AND_MY_TOOLS,
  },
  PRACTICE: {
    id: 'practice',
    path: '/practice',
    label: 'Practice',
    icon: School,
    showInBottomNav: true,
    bottomNavLabel: 'Practice',
    group: NAV_GROUPS.SUPPORT_AND_MY_TOOLS,
  },
  PROGRESS: {
    id: 'progress',
    path: '/progress',
    label: 'Progress',
    icon: Insights,
    showInBottomNav: true,
    bottomNavLabel: 'Progress',
    group: NAV_GROUPS.SUPPORT_AND_MY_TOOLS,
  },
  TAG_MANAGEMENT: {
    id: 'syllabus',
    path: '/syllabus',
    label: 'Curriculum Review',
    icon: Settings,
    showInBottomNav: false,
    group: NAV_GROUPS.SUPPORT_AND_MY_TOOLS,
  },
  JOIN_CLASS: {
    id: 'join-class',
    path: '/class',
    search: '?inApp=1',
    label: 'Join Class',
    icon: Videocam,
    showInBottomNav: false,
    group: NAV_GROUPS.SUPPORT_AND_MY_TOOLS,
  },

  // --- Not reachable by super_admin: grouped for the admin drawer only ----
  // (teacher/student drawers are flat — see ROLE_NAV_ORDER.)
  TASKS: {
    id: 'tasks',
    path: '/tasks',
    label: 'Tasks',
    icon: ChecklistRounded,
    showInBottomNav: true,
    bottomNavLabel: 'Tasks',
    group: NAV_GROUPS.SUPPORT_AND_MY_TOOLS,
  },
  TEACHER_HOMEWORK: {
    id: 'teacher-homework',
    path: '/teacher/homework',
    label: 'Assign Homework',
    icon: Assignment,
    showInBottomNav: false,
    group: NAV_GROUPS.CONTENT_AND_REVIEW,
  },
};

export const AUTH_ITEMS: Record<string, AuthItem> = {
  LOGIN: {
    id: 'login',
    label: 'Log In',
    icon: Login,
  },
  LOGOUT: {
    id: 'logout',
    label: 'Log Out',
    icon: Logout,
  },
};

const MORE_MENU_ICON: MoreMenuItem = {
  id: 'more',
  label: 'More',
  icon: MenuIcon,
};

/**
 * Cap the mobile bottom bar at MAX_BOTTOM_NAV_PRIMARY primary items for
 * non-admin roles (students, teachers). With the trailing "More" entry the
 * bar renders MAX_BOTTOM_NAV_PRIMARY + 1 = 5 buttons total. Anything that
 * would have been the 5th+ primary item falls back into the More drawer via
 * getHamburgerMenuItems. 5 buttons is the ceiling the bar can style at 320px
 * (see MobileBottomNavigation) — raising this again needs a layout rethink.
 */
export const MAX_BOTTOM_NAV_PRIMARY = 4;

const getPrimaryBottomNavItems = (
  userRole: RoleId | string,
  user?: NavUser | null
): NavigationItem[] =>
  getNavigationItems(userRole, true, user).slice(0, MAX_BOTTOM_NAV_PRIMARY);

/**
 * Per-role display order for the flat nav list, keyed by item id (lower =
 * first). Items absent from a role's map sort after the mapped ones, preserving
 * their object-definition order (stable sort). Roles without an entry — admin /
 * super_admin — keep the grouped-nav ordering (NAVIGATION_GROUPS) untouched, so
 * this only reshuffles the flat list teachers/students see.
 */
const ROLE_NAV_ORDER: Partial<Record<RoleId, Record<string, number>>> = {
  [ROLES.TEACHER]: {
    'teacher-class-codes': 0, // My Classes (first phone bottom-bar slot)
    'teacher-students': 1, // Students (first where class codes are hidden)
    'nonprofit-organizations-admin': 2, // Delegated nonprofit administration
    'help-requests': 3, // Help Requests
    'teacher-questions': 4, // My Questions
    'lesson-prep': 5, // Lesson Prep
    'teacher-homework': 6, // Assign Homework
    practice: 7, // Practice
    'join-class': 8, // Join Class
  },
  // Student order is the intent ladder the IA split is built on: do now →
  // choose → see state → look back. My Requests sits fifth so it overflows
  // past the bottom-bar cap into More.
  [ROLES.USER]: {
    tasks: 0, // Tasks
    practice: 1, // Practice
    progress: 2, // Progress
    history: 3, // History
    'my-requests': 4, // My Requests
  },
};

/**
 * Get navigation items filtered by user role and feature flags
 */
export const getNavigationItems = (
  userRole: RoleId | string,
  bottomNavOnly = false,
  user?: NavUser | null
): NavigationItem[] => {
  const items = Object.values(NAVIGATION_ITEMS).filter(item => {
    const hasAccess = hasRouteAccess(
      userRole as any,
      item.path,
      (user ?? undefined) as any
    );
    const showInContext = bottomNavOnly ? item.showInBottomNav : true;

    if (isRouteHiddenByTeamPolicy(item.path, user?.teamPolicy)) {
      return false;
    }

    // Check feature flag requirement if specified (super_admin bypasses the
    // gate for the routes in SUPER_ADMIN_FEATURE_BYPASS — mirrors hasRouteAccess).
    if (item.requiredFeature) {
      const superAdminBypass =
        userRole === ROLES.SUPER_ADMIN &&
        SUPER_ADMIN_FEATURE_BYPASS.has(item.path);
      const hasFeature =
        superAdminBypass ||
        hasEffectiveFeatureAccess(user as any, item.requiredFeature);
      return hasAccess && showInContext && hasFeature;
    }

    return hasAccess && showInContext;
  });

  const order = ROLE_NAV_ORDER[userRole as RoleId];
  if (!order) return items;
  const rank = (item: NavigationItem) =>
    order[item.id] ?? Number.MAX_SAFE_INTEGER;
  return [...items].sort((a, b) => rank(a) - rank(b));
};

/**
 * Get bottom navigation items for a specific user role
 */
export const getBottomNavItems = (
  userRole: RoleId | string,
  user?: NavUser | null
): (NavigationItem | MoreMenuItem)[] => {
  // Admin and super_admin get a curated set: Leads, Enrollments, Class Codes, More
  if (userRole === ROLES.ADMIN || userRole === ROLES.SUPER_ADMIN) {
    const allItems = getNavigationItems(userRole, false, user);
    const adminItems = allItems
      .filter(item => item.adminBottomNav)
      .sort(
        (a, b) => (a.adminBottomNavOrder ?? 0) - (b.adminBottomNavOrder ?? 0)
      );
    return [...adminItems, MORE_MENU_ICON];
  }

  const items = getPrimaryBottomNavItems(userRole, user);

  // Add More menu for all authenticated roles to access hamburger menu
  if (userRole) {
    return [...items, MORE_MENU_ICON];
  }

  return items;
};

/**
 * Get hamburger menu items (excluding bottom nav items).
 * For admin/super_admin: excludes items shown in the admin bottom nav (adminBottomNav).
 * For other roles: excludes items shown in the standard bottom nav (showInBottomNav).
 */
export const getHamburgerMenuItems = (
  userRole: RoleId | string,
  user: NavUser | null = null
): NavigationItem[] => {
  const isAdmin = userRole === ROLES.ADMIN || userRole === ROLES.SUPER_ADMIN;
  if (isAdmin) {
    return getNavigationItems(userRole, false, user).filter(
      item => !item.adminBottomNav
    );
  }
  // For non-admin roles the bottom bar is capped, so items with
  // showInBottomNav=true that were sliced out (overflow) must reappear here.
  const primaryIds = new Set(
    getPrimaryBottomNavItems(userRole, user).map(item => item.id)
  );
  return getNavigationItems(userRole, false, user).filter(
    item => !primaryIds.has(item.id)
  );
};

/**
 * Bucket filtered items by group and return only non-empty groups in display order.
 */
const groupItemsByNavGroup = (items: NavigationItem[]): GroupedNavSection[] => {
  const byGroup = new Map<NavGroupId, NavigationItem[]>();
  for (const item of items) {
    const gid = item.group ?? NAV_GROUPS.SUPPORT_AND_MY_TOOLS;
    if (!byGroup.has(gid)) byGroup.set(gid, []);
    byGroup.get(gid)!.push(item);
  }
  return [...NAVIGATION_GROUPS]
    .sort((a, b) => a.order - b.order)
    .filter(g => byGroup.has(g.id) && (byGroup.get(g.id)?.length ?? 0) > 0)
    .map(g => ({
      groupId: g.id,
      groupLabel: g.label,
      items: byGroup.get(g.id)!,
    }));
};

/**
 * Get navigation items grouped by function (for drawer/hamburger with section headers).
 */
export const getGroupedNavigationItems = (
  userRole: RoleId | string,
  bottomNavOnly = false,
  user?: NavUser | null
): GroupedNavSection[] => {
  const items = getNavigationItems(userRole, bottomNavOnly, user);
  return groupItemsByNavGroup(items);
};

/**
 * Get hamburger menu items grouped by function (non–bottom-nav only).
 */
export const getGroupedHamburgerMenuItems = (
  userRole: RoleId | string,
  user: NavUser | null = null
): GroupedNavSection[] => {
  const items = getHamburgerMenuItems(userRole, user);
  return groupItemsByNavGroup(items);
};
